
import { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';

export type DataContextType = {
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
  // Add context for data cache management
  clearCache: (namespace?: string) => void;
};

const DataContext = createContext<DataContextType>({
  isLoading: false,
  setIsLoading: () => {},
  clearCache: () => {},
});

export function DataProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(false);
  
  // Cache management function - memoized to prevent recreating on each render
  const clearCache = useCallback((namespace?: string) => {
    // This will be hooked up to the cache invalidation in useCachedData
    if (typeof window !== 'undefined') {
      if (namespace) {
        // Clear specific cache entries
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith(`data_cache_${namespace}`)) {
            localStorage.removeItem(key);
          }
        });
      } else {
        // Clear all cache entries
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('data_cache_')) {
            localStorage.removeItem(key);
          }
        });
      }
    }
  }, []);
  
  // Memoize the context value to prevent unnecessary rerenders
  const contextValue = useMemo(() => ({
    isLoading,
    setIsLoading,
    clearCache,
  }), [isLoading, clearCache]);
  
  return (
    <DataContext.Provider value={contextValue}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}

// Export for App.tsx
export { DataContext };
